/**
 * Thin GraphQL client for the ai-managed-object-service, accessed
 * via the Atlassian GraphQL Gateway (AGG).
 *
 * This module is intentionally kept framework-agnostic (no React hooks)
 * so it can be called from both React components and editor plugin
 * imperative code paths.
 */

const AGG_GRAPHQL_ENDPOINT = '/gateway/api/graphql';
const AGG_GRAPHQL_SLOW_ENDPOINT = `${AGG_GRAPHQL_ENDPOINT}/slow`;

export interface GraphqlResponse<TData> {
	data?: TData;
	errors?: Array<{ extensions?: Record<string, unknown>; message: string }>;
}

export interface ExecuteGraphqlOptions {
	slow?: boolean;
}

/**
 * Execute a GraphQL operation against AGG.
 */
export async function executeGraphql<TData, TVariables = Record<string, unknown>>(
	query: string,
	variables: TVariables,
	options: ExecuteGraphqlOptions = {},
): Promise<GraphqlResponse<TData>> {
	const { slow = false } = options;
	const endpoint = slow ? AGG_GRAPHQL_SLOW_ENDPOINT : AGG_GRAPHQL_ENDPOINT;

	const response = await fetch(endpoint, {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({ query, variables }),
	});

	if (!response.ok) {
		throw new AiManagedObjectApiError(
			`Request failed with status ${response.status}`,
			response.status,
		);
	}

	return (await response.json()) as GraphqlResponse<TData>;
}

/**
 * Error class for API failures. Carries the HTTP status when available
 * so callers can distinguish 404s from other failures.
 */
export class AiManagedObjectApiError extends Error {
	public readonly statusCode: number | undefined;

	constructor(message: string, statusCode?: number) {
		super(message);
		this.name = 'AiManagedObjectApiError';
		this.statusCode = statusCode;
	}
}
